'use client';

import type { FC, HTMLAttributes } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

const noop = () => {};

interface InfiniteListContextType {
  loadMore: () => void;
  isPending: boolean;
  hasMore: boolean;
}

export const InfiniteListContext = createContext<InfiniteListContextType>({
  loadMore: noop,
  isPending: false,
  hasMore: false,
});

interface InfiniteListRootProps extends HTMLAttributes<HTMLUListElement> {
  loadMore?: () => void;
  isPending?: boolean;
  hasMore?: boolean;
}

export const Root: FC<InfiniteListRootProps> = ({
  loadMore,
  isPending = false,
  hasMore = false,
  className,
  children,
  ...props
}) => {
  const context: InfiniteListContextType = useMemo(
    () => ({
      loadMore: loadMore ?? noop,
      isPending,
      hasMore,
    }),
    [loadMore, isPending, hasMore],
  );

  return (
    <InfiniteListContext.Provider value={context}>
      <ul className={twMerge('relative', className)} {...props}>
        {children}
      </ul>
    </InfiniteListContext.Provider>
  );
};

Root.displayName = 'InfiniteListRoot';

export const useInfiniteListContext = () => useContext(InfiniteListContext);
